import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { currentContextAtom, deliveryContactsAtom, invoicingContactsAtom } from '../../../state';
import { UserRoleDocument } from '../../../queries/__generated__/userRole.generated';
import { useUpdateCompanyContactsMutation } from '../../../mutations/__generated__/updateCompanyContacts.generated';
import { commonSnackbarPropsAtom } from '../../../common/state/state';
import useOpenable from '../../../hooks/useOpenable';
import FormDrawer from '../FormDrawer';
import { canEditCompanySettingsAtom } from '../../state';
import ContactsForm from './ContactsForm';
import ContactList from './ContactList';

export type ContactsType = 'DELIVERY' | 'INVOICING';

export interface ContactsInfoProps {
  type: ContactsType;
}

const TypeContextData = {
  DELIVERY: {
    contactsAtom: deliveryContactsAtom,
    updateContactKey: 'is_delivery_contact',
    newContactUpdatedTrans: 'settings:delivery_contacts_successfully_updated',
    newContactAddedTrans: 'settings:delivery_contacts_successfully_added',
    addContactTrans: 'settings:add_delivery_contact',
    addContactTransDescription: 'settings:add_delivery_contact_description',
  },
  INVOICING: {
    contactsAtom: invoicingContactsAtom,
    updateContactKey: 'is_invoicing_contact',
    newContactUpdatedTrans: 'settings:invoicing_contacts_successfully_updated',
    newContactAddedTrans: 'settings:invoicing_contacts_successfully_added',
    addContactTrans: 'settings:add_invoicing_contact',
    addContactTransDescription: 'settings:add_invoicing_contact_description',
  },
};

export default function ContactsInfo({ type }: ContactsInfoProps): React.JSX.Element {
  const { contactsAtom, updateContactKey, newContactUpdatedTrans, newContactAddedTrans, addContactTrans, addContactTransDescription } = TypeContextData[type];
  const { companyId } = useAtomValue(currentContextAtom);
  const { t } = useTranslation(['common', 'settings']);
  const contacts = useAtomValue(contactsAtom);
  const canEdit = useAtomValue(canEditCompanySettingsAtom);
  const { open, close, isOpen } = useOpenable();
  const [updateCompanyContacts, { data: upsertCompanyContactsData }] = useUpdateCompanyContactsMutation({
    refetchQueries: [UserRoleDocument],
  });
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  const onSubmit = async (newContactUserIds: string[]): Promise<void> => {
    const formerContactUserIds = contacts.reduce((acc, { userId }) => {
      if (!newContactUserIds.includes(userId)) {
        acc.push(userId);
      }

      return acc;
    }, [] as string[]);

    updateCompanyContacts({
      variables: {
        formerContactUserIds,
        formerContactExp: {
          [updateContactKey]: false,
        },
        newContactUserIds,
        newContactExp: {
          [updateContactKey]: true,
        },
        companyId,
      },
    });
  };

  useEffect((): void => {
    if (upsertCompanyContactsData?.new_contacts_update || upsertCompanyContactsData?.former_contacts_update) {
      setCommonSnackbarProps({
        label: t(contacts.length ? newContactUpdatedTrans : newContactAddedTrans),
        snackbarProps: {
          open: true,
        },
      });
      close();
    }
  }, [upsertCompanyContactsData]);

  return (
    <>
      <Box
        sx={{
          '& .MuiTypography-root': {
            lineHeight: 'normal',
          },
        }}
      >
        {
          contacts.length
            ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <ContactList contacts={contacts} />
                {canEdit && (
                  <Button
                    variant="tonal"
                    onClick={open}
                    startIcon={<EditOutlinedIcon />}
                    sx={{ alignSelf: 'end' }}
                  >
                    {t('common:modify')}
                  </Button>
                )}
              </Box>
            )
            : (
              <>
                <Typography variant="titleMedium" sx={{ mb: 1 }}>
                  {t('settings:no_contact_registered')}
                </Typography>
                {canEdit && (
                  <>
                    <Typography variant="bodyMedium" color="secondary">
                      {t(addContactTransDescription)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: 1.5 }}>
                      <Button
                        variant="tonal"
                        startIcon={<AddIcon />}
                        onClick={open}
                      >
                        {t('common:add')}
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )
        }
      </Box>
      <FormDrawer
        title={t(addContactTrans)}
        open={isOpen}
        onClose={close}
        FormComponent={ContactsForm}
        formProps={{
          contacts,
          description: addContactTransDescription,
          onSubmit,
        }}
      />
    </>
  );
}
